import { useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client/react';
import ConfirmDialog from '../../components/ConfirmDialog';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDate } from '../../Formatters';
import { SubscriptionModel } from '../../interfaces';
import { DELETE_USAGE_LIMIT, GET_USAGE_LIMITS } from '../../usagelimits/usagelimits.graphql';
import { RuleDialog } from '../../usagelimits/RuleDialog';
import { sourceOfProduct } from '../../usagelimits/products';
import { UsageLimitRule } from '../../usagelimits/interfaces';

export function subscriptionRules(rules: UsageLimitRule[], subscriptionId: number) {
  return rules.filter((r) => !r.userId && !r.licenseId && r.subscriptionIds.includes(subscriptionId));
}

function globalRules(rules: UsageLimitRule[], sourceId: number) {
  return rules.filter((r) => !r.userId && !r.licenseId && r.subscriptionIds.length === 0 && (r.sourceId == null || r.sourceId === sourceId));
}

interface Props {
  subscription: SubscriptionModel;
  subscriptions: SubscriptionModel[];
}

export function UsageLimitsPanel({ subscription, subscriptions }: Props) {
  const { data, loading, error } = useQuery<{ usageLimits: UsageLimitRule[] }>(GET_USAGE_LIMITS);
  const [dialog, setDialog] = useState<'closed' | 'new' | UsageLimitRule>('closed');
  const [toDelete, setToDelete] = useState<UsageLimitRule | null>(null);
  const [deleteRule] = useMutation<any, any>(DELETE_USAGE_LIMIT, { refetchQueries: [{ query: GET_USAGE_LIMITS }] });

  const sourceId = subscription.product ? sourceOfProduct(subscription.product) : null;
  const rules = data ? subscriptionRules(data.usageLimits, subscription.bksId) : [];
  const globals = data && sourceId != null ? globalRules(data.usageLimits, sourceId) : [];
  const nameOf = (id: number) => subscriptions.find((s) => s.bksId === id)?.name ?? `Abonnement ${id}`;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="body2" color="text.secondary">
          Grenser for hver bruker med abonnementet. En regel på lisensen eller brukeren overstyrer abonnementets. Endringer trer i kraft i portalen
          innen et par minutter.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          disabled={sourceId == null}
          onClick={() => setDialog('new')}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Ny bruksgrense
        </Button>
      </Stack>

      <Box sx={{ mt: 3 }}>
        {loading && <LoadingSpinner />}
        {error && <Alert severity="error">Fikk ikke hentet bruksgrensene.</Alert>}
        {data && (
          <Stack spacing={1.5}>
            {rules.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Abonnementet har ingen egne bruksgrenser.
              </Typography>
            )}
            {rules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                alsoFor={rule.subscriptionIds.filter((id) => id !== subscription.bksId).map(nameOf)}
                onEdit={() => setDialog(rule)}
                onDelete={() => setToDelete(rule)}
              />
            ))}
          </Stack>
        )}

        {globals.length > 0 && (
          <Stack spacing={1.5} sx={{ mt: 4 }}>
            <Typography variant="overline" color="text.secondary">
              Global grense, gjelder i tillegg
            </Typography>
            {globals.map((rule) => (
              <RuleCard key={rule.id} rule={rule} />
            ))}
          </Stack>
        )}
      </Box>

      {dialog !== 'closed' && sourceId != null && (
        <RuleDialog
          open
          rule={dialog === 'new' ? null : dialog}
          presetSourceId={sourceId}
          presetSubscriptionId={subscription.bksId}
          onClose={() => setDialog('closed')}
        />
      )}
      <ConfirmDialog
        open={!!toDelete}
        title="Slett bruksgrense"
        message={
          toDelete && toDelete.subscriptionIds.length > 1
            ? `Regelen gjelder også ${toDelete.subscriptionIds
                .filter((id) => id !== subscription.bksId)
                .map(nameOf)
                .join(', ')}, og slettes for dem også. Vil du slette den?`
            : 'Er du sikker på at du vil slette bruksgrensen?'
        }
        confirmText="Slett"
        confirm={async () => {
          await deleteRule({ variables: { input: { id: toDelete!.id } } });
          setToDelete(null);
        }}
        deny={() => setToDelete(null)}
      />
    </Box>
  );
}

function RuleCard({ rule, alsoFor, onEdit, onDelete }: { rule: UsageLimitRule; alsoFor?: string[]; onEdit?(): void; onDelete?(): void }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, px: 2, py: 1.5 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <Limit label="Anvisninger" max={rule.maxDocuments} days={rule.documentWindowDays} />
          <Limit label="KI-tokens" max={rule.maxAiTokens} days={rule.aiTokenWindowDays} />
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Chip size="small" label={rule.active ? 'Aktiv' : 'Inaktiv'} color={rule.active ? 'success' : 'default'} variant="outlined" />
          {onEdit && (
            <IconButton size="small" title="Endre" onClick={onEdit}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {onDelete && (
            <IconButton size="small" title="Slett" onClick={onDelete}>
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 1 }}>
        {alsoFor && alsoFor.length > 0 && <>Gjelder også {alsoFor.join(', ')} · </>}
        Sist endret {formatDate(rule.lastUpdatedAt)}
      </Typography>
    </Paper>
  );
}

function Limit({ label, max, days }: { label: string; max?: number | null; days?: number | null }) {
  return (
    <Box>
      <Typography variant="overline" component="div" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {label}
      </Typography>
      <Typography variant="body2">{max != null ? `${max.toLocaleString('nb-NO')} per ${days} dager` : 'Ingen grense'}</Typography>
    </Box>
  );
}
